import React, { useState, useEffect } from "react";
import "../../../css/SlotTemplateModal.css";
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';


interface Slot {
  startTime: string;
  endTime: string;
  slotType: string;
}

const initialSlots: Slot[] = [
  { startTime: "09:00", endTime: "10:30", slotType: "Work Slot" },
  { startTime: "10:30", endTime: "11:00", slotType: "Break" },
];

interface SlotTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateAdded: () => void;
}

const SlotTemplateModal: React.FC<SlotTemplateModalProps> = ({ isOpen, onClose, onTemplateAdded }) => {
  const [templateName, setTemplateName] = useState("");
  const [slots, setSlots] = useState<Slot[]>(initialSlots);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTemplateName("");
      setSlots(initialSlots);
    }
  }, [isOpen]);

  const addSlot = () => {
    setSlots([...slots, { startTime: "", endTime: "", slotType: "Work Slot" }]);
  };

  const removeSlot = (index: number) => {
    const updated = [...slots];
    updated.splice(index, 1);
    setSlots(updated);
  };

  const handleChange = (index: number, field: keyof Slot, value: string) => {
    const updated = [...slots];
    updated[index][field] = value;
    setSlots(updated);
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      showToast("Please enter a template name", 'error');
      return;
    }

    const token = sessionStorage.getItem("accessToken");
    if (!token) {
      showToast("No access token found. Please login again.", 'error');
      return;
    }

    // 🔑 Transform slots to backend field names
    const formattedSlots = slots.map((s) => ({
      start_time: s.startTime,
      end_time: s.endTime,
      slot_type: s.slotType,
    }));

    const payload = {
      name: templateName, // backend expects snake_case
      slots: formattedSlots,
    };

    try {
      setLoading(true);
      const res = await api.appointment_api.post("/api/slot-templates",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("✅ Created slot template:", res.data);
      showToast("Slot template created successfully!", 'success');
      onTemplateAdded();
      onClose();
    } catch (err: any) {
      console.error("❌ Error creating slot template:", err);
      showToast(err.response?.data?.message || "Something went wrong.", 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="slot-view-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="slot-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="slot-view-modal-header">
          <h2>Add New Slot Template</h2>
          <button className="slot-view-close-icon" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        <div className="slot-view-modal-body">
          <label className="slot-view-field-label">Slot Template Name *</label>
          <input
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter template name"
          className="slot-view-input"
        />

        <h3>Slot Grid Table</h3>
        <table className="slot-view-slot-table">
          <thead>
            <tr>
              <th>START TIME</th>
              <th>END TIME</th>
              <th>SLOT TYPE</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="time"
                    value={slot.startTime}
                    onChange={(e) =>
                      handleChange(index, "startTime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    type="time"
                    value={slot.endTime}
                    onChange={(e) =>
                      handleChange(index, "endTime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    value={slot.slotType}
                    onChange={(e) =>
                      handleChange(index, "slotType", e.target.value)
                    }
                  >
                    <option value="Work Slot">Work Slot</option>
                    <option value="Break">Break</option>
                    {/* <option value="Lunch Break">Lunch Break</option> */}
                  </select>
                </td>
                <td>
                  <button
                    className="slot-view-delete-btn"
                    onClick={() => removeSlot(index)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#d32f2f" viewBox="0 0 24 24">
                      <path d="M3 6h18v2H3V6zm2 3h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 9zm5 2v9h2v-9H10zm4 0v9h2v-9h-2zM9 4V2h6v2h5v2H4V4h5z" />
                    </svg>

                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button className="slot-view-add-slot" onClick={addSlot}>
          + Add Slot
        </button>

        <div className="slot-view-modal-actions">
          <button className="slot-view-close-btn" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="slot-view-create-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Slot"}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SlotTemplateModal;
